import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCw, Mail } from 'lucide-react';
import { ButtonStylized, ButtonStylizedType } from '../../ButtonStylized/ButtonStylized';
import { RootState } from '../../Store/store';

export enum AuthErrorType {
  Invalid = 'invalid',
  Expired = 'expired',
  Unauthorized = 'unauthorized'
}

interface PageAuthErrorProps {
  errorType?: AuthErrorType;
  onRetry?: () => void;
  onRequestNewLink?: () => void;
}

export const PageAuthError: React.FC<PageAuthErrorProps> = ({ 
  errorType = AuthErrorType.Invalid,
  onRetry,
  onRequestNewLink
}) => {
  const { currentGame } = useSelector((state: RootState) => state.gameSession);
  const primaryColor = currentGame?.colorPrimary || '#6B8E23';
  const secondaryColor = currentGame?.colorSecondary || '#8B4513';
  const errorConfig = {
    [AuthErrorType.Invalid]: {
      title: 'Invalid Login Link',
      message: 'This login link is not valid. It may have been already used or the link is malformed.',
      icon: AlertTriangle,
      iconColor: '#ef4444', // red-500
      canRetry: false,
      canRequestNew: true
    },
    [AuthErrorType.Expired]: {
      title: 'Login Link Expired',
      message: 'This login link has expired. Login links are valid for 15 minutes after being sent.',
      icon: RefreshCw,
      iconColor: '#f59e0b', // amber-500
      canRetry: false,
      canRequestNew: true
    },
    [AuthErrorType.Unauthorized]: {
      title: 'Session Expired',
      message: 'Your session has expired or is invalid. Please log in again to continue.',
      icon: AlertTriangle,
      iconColor: '#ef4444', // red-500
      canRetry: true,
      canRequestNew: false
    }
  };

  const config = errorConfig[errorType];
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Icon Container */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: `${config.iconColor}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px'
        }}
      >
        <Icon 
          size={40} 
          color={config.iconColor}
          strokeWidth={2}
        />
      </motion.div>

      {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: primaryColor,
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          {config.title}
        </h1>

        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: primaryColor,
          opacity: 0.9,
          textAlign: 'center',
          lineHeight: '1.6',
          marginBottom: '32px'
        }}>
          {config.message}
        </p>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {config.canRequestNew && (
            <ButtonStylized
              buttonType={ButtonStylizedType.Primary}
              text="Send New Link"
              icon={<Mail size={20} />}
              onClick={onRequestNewLink}
              additionalStyles={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px'
              }}
            />
          )}

          {config.canRetry && (
            <ButtonStylized
              buttonType={ButtonStylizedType.Primary}
              text="Try Again"
              icon={<RefreshCw size={20} />}
              onClick={onRetry}
              additionalStyles={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px'
              }}
            />
          )}
        </div>

        {/* Help Text */}
        <p style={{
          fontSize: '14px',
          color: primaryColor,
          opacity: 0.9,
          textAlign: 'center',
          marginTop: '24px',
          lineHeight: '1.5'
        }}>
          {errorType === AuthErrorType.Expired && 
            'Tip: Check your email for the most recent login link. Each link expires 15 minutes after being sent.'
          }
          {errorType === AuthErrorType.Invalid && 
            'Make sure you copied the entire link from your email. Login links can only be used once.'
          }
          {errorType === AuthErrorType.Unauthorized && 
            'Your authentication session has expired for security. Please log in again to continue playing.'
          }
        </p>
      </motion.div>
  );
};
